import { KeyboardEvent, MouseEvent, Ref } from "react";
import { Link } from "react-router";
import { ChevronDown, Globe } from "lucide-react";

import { CollectionNode } from "@/common/collectionTree";
import { collectionPath } from "@/common/urls";
import { cn } from "@/lib/utils";
import CollectionPinButton from "./CollectionPinButton";
import CollectionRowTooltip from "./CollectionRowTooltip";
import { SidebarMenuButton, SidebarMenuItem } from "../sidebar";

// One row is a fixed grid: guides, then a disclosure slot that is reserved
// whether or not the row has children, then the title. That reservation is the
// whole point — anything rendered in the title's flex flow (as the title-record
// glyph used to be) pushes one row's text out of line with its siblings'.
const BASE_X = 12;
const ROW_INDENT = 12;
const DISCLOSURE_W = 16;
const ROW_H = 29;

const rowIndent = (depth: number) => BASE_X + depth * ROW_INDENT;

/**
 * The vertical hairlines standing in for the edges above this row.
 *
 * Guide `i` is the edge into the ancestor at depth `i + 1`, so it is dashed when
 * that edge is implicit — a collection nested here because its title record was
 * filed above, rather than because anybody linked it.
 */
function Guides({ implicitPath }: { implicitPath: boolean[] }) {
  return (
    <>
      {implicitPath.map((implicit, index) => (
        <span
          key={index}
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-y-0 border-l border-node-line",
            implicit && "border-dashed",
          )}
          style={{ left: rowIndent(index) + DISCLOSURE_W / 2 }}
        />
      ))}
    </>
  );
}

export default function CollectionRow({
  node,
  isActive,
  isExpanded,
  count,
  canPin,
  onToggle,
  onActiveClick,
  ref,
}: {
  node: CollectionNode;
  isActive: boolean;
  isExpanded: boolean;
  /** Records in the current interval, or undefined when the collection has none. */
  count?: number;
  canPin: boolean;
  onToggle: (key: string) => void;
  /** Clicking the already-open collection runs this instead of navigating:
   * it shows all of the collection's records, or flips the time/notes tab. */
  onActiveClick?: (() => void) | null;
  ref?: Ref<HTMLLIElement>;
}) {
  const { collection, depth, implicitPath, key } = node;
  const hasChildren = node.children.length > 0;
  const isPinned = collection.pin_position !== null;

  const toggle = (event: MouseEvent | KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onToggle(key);
  };

  const handleRowKeyDown = (event: KeyboardEvent) => {
    if (!hasChildren) return;
    if (event.key === "ArrowRight" && !isExpanded) toggle(event);
    if (event.key === "ArrowLeft" && isExpanded) toggle(event);
  };

  return (
    <SidebarMenuItem ref={ref} className="group/collection">
      <Guides implicitPath={implicitPath} />
      <CollectionRowTooltip collection={collection} count={count}>
        <SidebarMenuButton
          asChild
          isActive={isActive}
          className="rounded-none p-0 text-[13px] text-ink2 data-[active=true]:font-semibold data-[active=true]:[box-shadow:inset_2px_0_0_var(--primary)]"
          style={{
            height: ROW_H,
            paddingLeft: rowIndent(depth) + DISCLOSURE_W,
            paddingRight: canPin ? 52 : 30,
          }}
        >
          <Link
            to={collectionPath(collection)}
            aria-current={isActive ? "page" : undefined}
            onClick={(event) => {
              if (!isActive || !onActiveClick) return;
              event.preventDefault();
              onActiveClick();
            }}
            onKeyDown={handleRowKeyDown}
          >
            <span className="truncate">{collection.title}</span>
          </Link>
        </SidebarMenuButton>
      </CollectionRowTooltip>

      {/* Sits over the link's left padding, so it never competes for the
          navigation click. */}
      {hasChildren && (
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-label={
            isExpanded
              ? `Collapse ${collection.title}`
              : `Expand ${collection.title}`
          }
          onClick={toggle}
          className="absolute top-0 flex items-center justify-center text-muted-foreground hover:text-foreground"
          style={{ left: rowIndent(depth), width: DISCLOSURE_W, height: ROW_H }}
        >
          <ChevronDown
            className={cn(
              "size-3 shrink-0 transition-transform",
              !isExpanded && "-rotate-90",
            )}
          />
        </button>
      )}

      <div
        className="pointer-events-none absolute right-2 top-0 flex items-center gap-1.5 text-muted-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground"
        style={{ height: ROW_H }}
      >
        {/* What folding this row away hides. Not a record count — those are
            per-collection and would double-count a record filed in both a
            parent and a child. */}
        {hasChildren && !isExpanded && (
          <span className="font-mono text-[10.5px] tabular-nums opacity-70">
            +{node.descendantCount}
          </span>
        )}
        {collection.is_public && <Globe size={12} strokeWidth={0.9} />}
        {count !== undefined && (
          <span className="font-mono text-[11px] tabular-nums">{count}</span>
        )}
        {canPin && (
          <CollectionPinButton
            collection={collection}
            className={cn(
              "pointer-events-auto",
              !isPinned &&
                "opacity-0 focus-visible:opacity-100 group-hover/collection:opacity-100",
            )}
          />
        )}
      </div>
    </SidebarMenuItem>
  );
}
